import { createAsyncThunk } from '@reduxjs/toolkit'
import { ServiceConfig } from '../services/config'
import { APIError, get } from '../services/crud'
import { GuestUserRecord } from './typings'
import buildQuery from 'odata-query'
import { IExportInput, ILead } from '@user/store/lead'
import { escapeODataString } from '@sales-center/common'

const leadCardsUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.lead.odata}`
const odataExportUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.lead.odataExport}`
const opportunityCardsUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.opportunity.odata}`
const odataOppExportUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.opportunity.odataExport}`

export interface GuestUserQueryParams {
  search?: string
  page?: number
  pageSize?: number
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  filter?: string
}

const baseExpand = [
  'assignee',
  'billToContact',
  'serviceContact',
  'services',
  'createdBy',
  'odataCustomFieldValues',
]

export const getGuestUserLeads = createAsyncThunk<
  { data: GuestUserRecord[]; totalCount: number },
  GuestUserQueryParams | void,
  { rejectValue: APIError }
>('guestUser/getLeads', async (params, thunkApi) => {
  const state = thunkApi.getState() as any
  const currentState = state.guestUser
  const currentUser = state.userState.user
  const {
    search = currentState.leadsSearchText,
    page = currentState.leadsPage - 1,
    pageSize = currentState.leadsPerPage,
    sortField = 'createdDate',
    sortDirection = 'desc',
    filter = currentState.leadsFilterSet,
  } = params || {}

  const queryOptions: any = {
    orderBy: currentState.leadsOrderBy || `${sortField} ${sortDirection}`,
    top: pageSize,
    skip: page * pageSize,
    count: true,
    expand: baseExpand, // Add expand to include assignee and services
  }

  // Always filter by createdBy for guest users
  const baseFilter = `(createdBy/id eq ${currentUser.userId})`

  // Add search filter if provided
  let searchFilter = ''
  if (search) {
    const lowerSearch = escapeODataString(search.toLowerCase())
    searchFilter = `
    contains(tolower(contactName), '${lowerSearch}') or
    contains(tolower(billToContactName), '${lowerSearch}') or
    contains(tolower(company), '${lowerSearch}') or
    contains(tolower(primaryContactSearchName), '${lowerSearch}') or
    contains(tolower(address), '${lowerSearch}') or
    contains(tolower(billToAddress), '${lowerSearch}') or
    contains(tolower(primaryContactSearchAddress), '${lowerSearch}')
  `
  }

  // Combine filters
  if (searchFilter) {
    queryOptions.filter = `(${baseFilter}) and (${searchFilter})`
  } else {
    queryOptions.filter = baseFilter
  }

  // Add additional filters if provided
  if (filter?.appliedCount > 0) {
    const normalized = filter.normalizedFilters.join(' and ')

    queryOptions.filter = queryOptions.filter
      ? `(${queryOptions.filter}) and (${normalized})`
      : normalized
  }

  const qstring = buildQuery(queryOptions)

  const response = await get(
    `${leadCardsUrl}${qstring}&cardCaptureRefresh=false&contactSearch=false`,
  )

  if (response.ok) {
    const data = await response.json()
    return {
      data: data.value || [],
      totalCount: data['@odata.count'] || 0,
    }
  }

  const errorData = await response.json()
  return thunkApi.rejectWithValue(errorData)
})

export const getGuestUserOpportunities = createAsyncThunk<
  { data: GuestUserRecord[]; totalCount: number },
  GuestUserQueryParams | void,
  { rejectValue: APIError }
>('guestUser/getOpportunities', async (params, thunkApi) => {
  const state = thunkApi.getState() as any
  const currentState = state.guestUser
  const currentUser = state.userState.user
  const {
    search = currentState.oppsSearchText,
    page = currentState.oppsPage - 1,
    pageSize = currentState.oppsPerPage,
    sortField = 'createdDate',
    sortDirection = 'desc',
    filter = currentState.oppsFilterSet,
  } = params || {}

  const queryOptions: any = {
    orderBy: currentState.oppsOrderBy || `${sortField} ${sortDirection}`,
    top: pageSize,
    skip: page * pageSize,
    count: true,
    expand: [...baseExpand, 'leadCreatedBy'], // Add expand to include assignee and services
  }

  // Always filter by createdBy for guest users
  const baseFilter = `(createdBy/id eq ${currentUser.userId})`

  // Add search filter if provided
  let searchFilter = ''
  if (search) {
    const lowerSearch = escapeODataString(search.toLowerCase())
    searchFilter = `
    contains(tolower(opportunityName), '${lowerSearch}') or
    contains(tolower(contactName), '${lowerSearch}') or
    contains(tolower(billToContactName), '${lowerSearch}') or
    contains(tolower(company), '${lowerSearch}') or
    contains(tolower(primaryContactSearchName), '${lowerSearch}') or
    contains(tolower(address), '${lowerSearch}') or
    contains(tolower(billToAddress), '${lowerSearch}') or
    contains(tolower(primaryContactSearchAddress), '${lowerSearch}')
  `
  }

  // Combine filters
  if (searchFilter) {
    queryOptions.filter = `(${baseFilter}) and (${searchFilter})`
  } else {
    queryOptions.filter = baseFilter
  }

  // Add additional filters if provided
  if (filter?.appliedCount > 0) {
    const normalized = filter.normalizedFilters.join(' and ')

    queryOptions.filter = queryOptions.filter
      ? `(${queryOptions.filter}) and (${normalized})`
      : normalized
  }

  const qstring = buildQuery(queryOptions)

  const response = await get(
    `${opportunityCardsUrl}${qstring}&cardCaptureRefresh=false&contactSearch=false`,
  )

  if (response.ok) {
    const data = await response.json()
    return {
      data: data.value || [],
      totalCount: data['@odata.count'] || 0,
    }
  }

  const errorData = await response.json()
  return thunkApi.rejectWithValue(errorData)
})

export const getGuestUserLeadListExport = createAsyncThunk<
  string,
  IExportInput,
  { rejectValue: APIError }
>('lead/cards/export', async (params, thunkApi) => {
  const state = thunkApi.getState() as any
  const currentState = state.guestUser
  const currentUser = state.userState.user

  const baseFilter = `(createdBy/id eq ${currentUser.userId})`

  const queryOptions: any = {
    skip: 0,
    top: 0,
    orderBy: currentState.leadsOrderBy || `createdDate desc`,
    expand: baseExpand,
  }

  let searchFilter = ''
  if (currentState.leadsSearchText) {
    const lowerSearch = escapeODataString(
      currentState.leadsSearchText.toLowerCase(),
    )
    searchFilter = `
    contains(tolower(contactName), '${lowerSearch}') or
    contains(tolower(billToContactName), '${lowerSearch}') or
    contains(tolower(company), '${lowerSearch}') or
    contains(tolower(primaryContactSearchName), '${lowerSearch}') or
    contains(tolower(address), '${lowerSearch}') or
    contains(tolower(billToAddress), '${lowerSearch}') or
    contains(tolower(primaryContactSearchAddress), '${lowerSearch}')
  `
  }

  // Combine filters
  if (searchFilter) {
    queryOptions.filter = `(${baseFilter}) and (${searchFilter})`
  } else {
    queryOptions.filter = baseFilter
  }

  // Add additional filters if provided
  if (currentState.leadsFilterSet.normalizedFilters > 0) {
    const normalized =
      currentState.leadsFilterSet.normalizedFilters.join(' and ')

    queryOptions.filter = queryOptions.filter
      ? `(${queryOptions.filter}) and (${normalized})`
      : normalized
  }

  const qstring = buildQuery<ILead>(queryOptions)

  const response = await get(
    `${odataExportUrl}${qstring}&selectedColumns=${params.columns}&showMultipleLocations=${params.multiServiceLocations}`,
  )

  if (response.ok) {
    return await (response as Response).text()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const getGuestUserOpportunityListExport = createAsyncThunk<
  string,
  IExportInput,
  { rejectValue: APIError }
>('lead/cards/export', async (params, thunkApi) => {
  const state = thunkApi.getState() as any
  const currentState = state.guestUser
  const currentUser = state.userState.user

  const baseFilter = `(createdBy/id eq ${currentUser.userId})`

  const queryOptions: any = {
    skip: 0,
    top: 0,
    orderBy: currentState.oppsOrderBy || `createdDate desc`,
    expand: baseExpand,
  }

  let searchFilter = ''
  if (currentState.oppsSearchText) {
    const lowerSearch = escapeODataString(
      currentState.oppsSearchText.toLowerCase(),
    )
    searchFilter = `
    contains(tolower(contactName), '${lowerSearch}') or
    contains(tolower(billToContactName), '${lowerSearch}') or
    contains(tolower(company), '${lowerSearch}') or
    contains(tolower(primaryContactSearchName), '${lowerSearch}') or
    contains(tolower(address), '${lowerSearch}') or
    contains(tolower(billToAddress), '${lowerSearch}') or
    contains(tolower(primaryContactSearchAddress), '${lowerSearch}')
  `
  }

  // Combine filters
  if (searchFilter) {
    queryOptions.filter = `(${baseFilter}) and (${searchFilter})`
  } else {
    queryOptions.filter = baseFilter
  }

  if (currentState.oppsFilterSet.normalizedFilters > 0) {
    const normalized =
      currentState.oppsFilterSet.normalizedFilters.join(' and ')

    queryOptions.filter = queryOptions.filter
      ? `(${queryOptions.filter}) and (${normalized})`
      : normalized
  }

  const qstring = buildQuery<ILead>(queryOptions)

  const response = await get(
    `${odataOppExportUrl}${qstring}&selectedColumns=${params.columns}&showMultipleLocations=${params.multiServiceLocations}`,
  )

  if (response.ok) {
    return await (response as Response).text()
  }
  return thunkApi.rejectWithValue(await response.json())
})
